import {
  Alert,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Snackbar,
  Typography,
} from '@mui/material'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import SupportRequestDialog from '../../help/SupportRequestDialog'
import { useUserSitesByOrg } from '../../user/hooks/user.hooks'
import { logger } from '../../utils/logger'

const HelpMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const TOPBAR_ICON_SIZE = '1.25rem'
  const [supportOpen, setSupportOpen] = useState(false)
  const handleClose = () => setAnchorEl(null)

  useEffect(() => {
    const openHandler = () => {
      console.log('[HelpMenu] open-help-menu event received')
      const btn = document.getElementById('help-topBar-button')
      if (btn) setAnchorEl(btn as HTMLElement)
    }
    const closeHandler = () => setAnchorEl(null)
    window.addEventListener('open-help-menu', openHandler)
    window.addEventListener('close-help-menu', closeHandler)
    return () => {
      window.removeEventListener('open-help-menu', openHandler)
      window.removeEventListener('close-help-menu', closeHandler)
    }
  }, [])

  const navigate = useNavigate()
  const sites = useUserSitesByOrg()
  const allSites = sites.flatMap((org) => org.sites)
  const hasSites = allSites.length > 0

  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const items = [
    {
      label: t('help.myOrganization'),
      icon: 'bi bi-building',
      href: null,
      disabled: !hasSites,
      onClick: () => {
        if (!hasSites) {
          setSnackbarOpen(true)
          return
        }
        navigate('/hub')
        handleClose()
      },
    },
    { label: t('help.helpCenter'), icon: 'bi bi-book', href: 'https://help.pmpsolutions.ca' },
    {
      label: t('help.supportRequests'),
      icon: 'bi bi-question-circle',
      href: null,
      onClick: () => {
        setSupportOpen(true)
        handleClose()
      },
    },
    {
      label: t('help.bookMeeting'),
      icon: 'bi bi-calendar',
      href: 'https://outlook.office365.com/book/PMPSolutions4@pmpsolutions.ca/',
    },
    { label: t('help.email'), icon: 'bi bi-envelope', href: 'mailto:support@pmpsolutions.ca' },
  ]

  return (
    <>
      <IconButton id="help-topBar-button" color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <i className="bi bi-question-circle" style={{ color: 'white', fontSize: TOPBAR_ICON_SIZE }} />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box id="help-menu-popover" sx={{ width: 260, p: 1 }}>
          <List disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={item.label}
                component={item.href ? 'a' : 'div'}
                href={item.href ?? undefined}
                target={item.href ? '_blank' : undefined}
                onClick={() => {
                  item.onClick?.()
                  if (item.href) handleClose()
                }}
                sx={{
                  opacity: item.disabled ? 0.5 : 1,
                  cursor: 'pointer',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <i className={item.icon} style={{ fontSize: '1.1rem' }} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <i className="bi bi-telephone" style={{ fontSize: '1rem' }} />
              <Typography variant="body2">581-891-7677</Typography>
            </Box>
            <Typography variant="body2">
              <a href="https://pmpsolutions.ca" target="_blank" rel="noopener noreferrer">
                {t('help.visit')}
              </a>
            </Typography>
          </Box>
        </Box>
      </Popover>

      <SupportRequestDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        onSubmit={(data) => {
          logger.log('support submitted', data)
          setSupportOpen(false)
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setSnackbarOpen(false)}>
          {t('help.noSites')}
        </Alert>
      </Snackbar>
    </>
  )
}

export default HelpMenu
